import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Card, 
  Typography, 
  Form, 
  Input, 
  Button, 
  Space, 
  message, 
  Spin,
  Upload,
  Modal,
  InputNumber,
  Row,
  Col,
  Select,
  List,
  Tag
} from 'antd';
import { 
  ArrowLeftOutlined, 
  SaveOutlined,
  DownloadOutlined,
  DeleteOutlined,
  FileOutlined,
  PictureOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { publicationService } from '../../services/publicationService';
import { attachmentService } from '../../services/attachmentService';

import { Publication, Attachment, PublicationCategory } from '../../types';

import dayjs from 'dayjs';
import '../../styles/contentStyles.css';
import { insertTable } from '../../utils/tableUtils';
import { applyStyle, removeStyle, insertImage } from '../../utils/styleUtils';


const { Text } = Typography;



const PublicationEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [form] = Form.useForm();
  const [publication, setPublication] = useState<Publication | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [uploading, setUploading] = useState(false);
  const [imageModalVisible, setImageModalVisible] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const [imageWidth, setImageWidth] = useState(300);
  const [imageHeight, setImageHeight] = useState(200);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>('');
  const [imageResizeModalVisible, setImageResizeModalVisible] = useState(false);
  const [selectedImage, setSelectedImage] = useState<HTMLImageElement | null>(null);
  const [tableModalVisible, setTableModalVisible] = useState(false);
  const [tableRows, setTableRows] = useState(2);
  const [tableCols, setTableCols] = useState(2);

  
  const contentRef = useRef<HTMLDivElement>(null);
  const lastContentRef = useRef<string>('');

  
  const isCreate = id === 'create';


  
  // 일반적인 표 삽입 함수
  const handleInsertTable = () => {
    const updatedContent = insertTable(contentRef, tableRows, tableCols);
    // 비동기적으로 폼 값 업데이트하여 순환 참조 방지
    setTimeout(() => {
      form.setFieldValue('content', updatedContent);
    }, 0);
    setTableModalVisible(false);
  };

  const fetchPublication = useCallback(async (id: string) => {
    setLoading(true);
    try {
      const data = await publicationService.getPublication(id);
      setPublication(data);
      
      // 첨부파일도 함께 조회
      try {
        const attachmentData = await attachmentService.getPublicationAttachments(id);
        setAttachments(attachmentData);
      } catch (error) {
        setAttachments(data.attachments || []);
      }
      
      form.setFieldsValue({
        title: data.title,
        content: data.content,
        category: data.category,
      });

      // Form 설정 후 contentEditable div 초기화
      setTimeout(() => {
        if (contentRef.current && data.content) {
          contentRef.current.innerHTML = data.content;
          lastContentRef.current = data.content;
        }
      }, 0);
    } catch (error: any) {
      message.error(t('error.publicationLoad'));
    } finally {
      setLoading(false);
    }
  }, [form, t]);

  useEffect(() => {
    if (!isCreate && id) {
      fetchPublication(id);
    } else if (isCreate) {
      // 생성 모드: 빈 내용으로 초기화
      if (contentRef.current) {
        contentRef.current.innerHTML = '';
        lastContentRef.current = '';
      }
    }
  }, [id, isCreate, fetchPublication]);

  // 표 행 추가/삭제 버튼 동작을 위한 직접 이벤트 리스너 등록
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.classList.contains('add-row-btn')) {
        e.preventDefault();
        e.stopPropagation();
        
        const controlsDiv = target.closest('.table-controls');
        if (!controlsDiv) return;
        
        const table = controlsDiv.previousElementSibling as HTMLTableElement;
        if (!table) return;
        
        const tbody = table.querySelector('tbody');
        if (!tbody) return;
        
        // 개정사항 표인지 일반적인 표인지 확인
        const isAmendmentTable = table.classList.contains('amendment-table') || 
                                table.closest('.amendment-table');
        
        if (isAmendmentTable) {
          // 개정사항 표 (2열 고정)
          const newRow = document.createElement('tr');
          newRow.innerHTML = `
            <td contenteditable="true"></td>
            <td contenteditable="true"></td>
          `;
          tbody.appendChild(newRow);
        } else {
          // 일반적인 표 (동적 열 수)
          const firstRow = tbody.querySelector('tr');
          if (firstRow) {
            const colCount = firstRow.children.length;
            const newRow = document.createElement('tr');
            let rowHTML = '';
            for (let i = 0; i < colCount; i++) {
              rowHTML += '<td contenteditable="true"></td>';
            }
            newRow.innerHTML = rowHTML;
            tbody.appendChild(newRow);
          }
        }
        
        // 폼 값 업데이트 (비동기적으로)
        if (contentRef.current) {
          setTimeout(() => {
            form.setFieldValue('content', contentRef.current!.innerHTML);
          }, 0);
        }
      } else if (target.classList.contains('delete-row-btn')) {
        e.preventDefault();
        e.stopPropagation();
        
        const controlsDiv = target.closest('.table-controls');
        if (!controlsDiv) return;
        
        const table = controlsDiv.previousElementSibling as HTMLTableElement;
        if (!table) return;
        
        const tbody = table.querySelector('tbody');
        if (!tbody || tbody.children.length <= 1) return;
        
        const lastChild = tbody.lastElementChild;
        if (lastChild) {
          tbody.removeChild(lastChild);
          
          // 폼 값 업데이트 (비동기적으로)
          if (contentRef.current) {
            setTimeout(() => {
              form.setFieldValue('content', contentRef.current!.innerHTML);
            }, 0);
          }
        }
      }
    };
    
    // document에 이벤트 리스너 등록 (더 안정적)
    document.addEventListener('click', handler);
    
    return () => {
      document.removeEventListener('click', handler);
    };
  }, [form, t]);

  const handleSave = async (values: { title: string; content: string }) => {
    setSaving(true);
    try {
      if (publication) {
        // 기존 공포·공시 수정
        await publicationService.updatePublication(publication.publication_id, values);
        message.success(t('success.publicationUpdate'));
        navigate(`/publications/${publication.publication_id}`);
      } else {
        // 새 공포·공시 생성
        await publicationService.createPublication(values);
        message.success(t('success.publicationCreate'));
        navigate('/publications');
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || t('error.publicationSave'));
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (publication) {
      navigate(`/publications/${publication.publication_id}`);
    } else {
      navigate('/publications');
    }
  };



  const handleInsertImage = useCallback(async () => {
    if (!imageUrl.trim() && !imageFile) {
      message.error(t('error.imageUrlOrFile'));
      return;
    }

    // 이미지 파일이 있는 경우 서버에 업로드
    if (imageFile) {
      try {
        if (!publication?.publication_id) {
          message.error(t('error.savePublicationFirst'));
          return;
        }

        const uploadedImage = await attachmentService.uploadEditorImage(
          imageFile, 
          'publication', 
          publication.publication_id
        );
        
        // 업로드된 이미지 URL을 사용하여 삽입
        insertImage(uploadedImage.file_path, form, contentRef);
        message.success(t('success.imageUpload'));
      } catch (error: any) {
        message.error(error.response?.data?.message || t('error.imageUpload'));
        return;
      }
    } else {
      // URL 이미지 삽입
      insertImage(imageUrl, form, contentRef);
    }
    
    setImageModalVisible(false);
    setImageUrl('');
    setImageFile(null);
    setImagePreview('');
  }, [imageUrl, imageFile, imagePreview, form, t, publication]);

  const handleFileUpload = async (file: File) => {
    // 새 공포·공시 작성 중인 경우 (publication이 없음)
    if (!publication) {
      message.error(t('error.savePublicationFirst'));
      return;
    }

    // 파일 크기 체크 (10MB)
    if (file.size > 10 * 1024 * 1024) {
      message.error(t('error.fileSize'));
      return;
    }



    setUploading(true);
    try {
      const uploadedAttachment = await attachmentService.uploadAttachment({
        file,
        publicationId: publication.publication_id
      });
      
      const newAttachments = [...attachments, uploadedAttachment];
      setAttachments(newAttachments);
      
      message.success(`${file.name} ${t('success.fileUpload')}`);
    } catch (error: any) {
      message.error(error.response?.data?.message || t('error.fileUpload'));
    } finally {
      setUploading(false);
    }
  };

  const handleFileDelete = async (attachment: Attachment) => {
    try {
      await attachmentService.deleteAttachment(attachment.attachment_id);
      const newAttachments = attachments.filter(a => a.attachment_id !== attachment.attachment_id);
      setAttachments(newAttachments);
      message.success(t('success.fileDelete'));
    } catch (error: any) {
      message.error(t('error.fileDelete'));
    }
  };

  const handleFileDownload = async (attachment: Attachment) => {
    try {
      attachmentService.downloadAttachmentDirect(attachment.attachment_id, attachment.file_name);
    } catch (error: any) {
      message.error(t('error.fileDownload'));
    }
  };

  // 파일 크기 포맷팅
  const formatFileSize = (bytes: number): string => {
    return attachmentService.formatFileSize(bytes);
  };

  const handleImageFileChange = (file: File) => {
    // 이미지 파일인지 확인
    if (!file.type.startsWith('image/')) {
      message.error(t('error.imageFile'));
      return;
    }

    // 파일 크기 제한 (5MB)
    if (file.size > 5 * 1024 * 1024) {
      message.error(t('error.imageSize'));
      return;
    }

    setImageFile(file);
    
    // 미리보기 생성
    const reader = new FileReader();
    reader.onload = (e) => {
      setImagePreview(e.target?.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleImageFileRemove = () => {
    setImageFile(null);
    setImagePreview('');
  };

  const handleImageResize = () => {
    if (!selectedImage) return;

    const newWidth = imageWidth;
    const newHeight = imageHeight;

    selectedImage.style.width = `${newWidth}px`;
    selectedImage.style.height = `${newHeight}px`;
    selectedImage.style.maxWidth = '100%';

    // form 업데이트
    const contentElement = contentRef.current;
    if (contentElement) {
      form.setFieldValue('content', contentElement.innerHTML);
    }

    setImageResizeModalVisible(false);
    setSelectedImage(null);
    message.success(t('success.imageResize'));
  };

  const handleAutoResize = () => {
    if (!selectedImage) return;

    const maxWidth = 800;
    const maxHeight = 600;
    
    const naturalWidth = selectedImage.naturalWidth;
    const naturalHeight = selectedImage.naturalHeight;
    
    let { width, height } = { width: naturalWidth, height: naturalHeight };
    
    // 비율을 유지하면서 크기 조정
    if (width > maxWidth) {
      height = (height * maxWidth) / width;
      width = maxWidth;
    }
    
    if (height > maxHeight) {
      width = (width * maxHeight) / height;
      height = maxHeight;
    }
    
    // 이미지가 최대 크기보다 작을 때는 원본 크기 유지하되 비율은 보장
    selectedImage.style.width = `${width}px`;
    selectedImage.style.height = `${height}px`;
    selectedImage.style.maxWidth = '100%';
    
    // form 업데이트
    const contentElement = contentRef.current;
    if (contentElement) {
      form.setFieldValue('content', contentElement.innerHTML);
    }
    
    message.success(t('success.autoResize'));
    
    // 모달 닫기
    setImageResizeModalVisible(false);
    setSelectedImage(null);
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
        <div style={{ marginTop: '16px' }}>{t('loading.publicationLoad')}</div>
      </div>
    );
  }

  return (
    <div>

      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <Button 
            icon={<ArrowLeftOutlined />} 
            onClick={handleCancel}
            style={{ marginRight: 16 }}
          >
            {t('button.cancel')}
          </Button>
          
          <Space>
            <Button 
              type="primary" 
              icon={<SaveOutlined />} 
              onClick={() => {
                // contentEditable div의 내용을 폼에 반영
                if (contentRef.current) {
                  form.setFieldValue('content', contentRef.current.innerHTML);
                }
                form.submit();
              }}
              loading={saving}
            >
              {t('button.save')}
            </Button>
          </Space>
        </div>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSave}
        >
          <Form.Item
            name="category"
            label={t('form.category')}
            rules={[{ required: true, message: t('validation.category') }]}
          >
            <Select placeholder={t('select.category')}>
              <Select.Option value={PublicationCategory.PUBLICATION}>
                {t('publications.categories.PUBLICATION')}
              </Select.Option>
              <Select.Option value={PublicationCategory.ANNOUNCEMENT}>
                {t('publications.categories.ANNOUNCEMENT')}
              </Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="title"
            label={t('form.title')}
            rules={[{ required: true, message: t('validation.title') }]}
          >
            <Input placeholder={t('input.title')} />
          </Form.Item>

          <Form.Item
            name="content"
            label={t('form.content')}
            rules={[{ required: true, message: t('validation.content') }]}
          >
            {/* 스타일 툴바 - 내용 영역 바로 위에 위치 */}
            <div style={{
              backgroundColor: '#f5f5f5',
              border: '1px solid #d9d9d9',
              borderBottom: 'none',
              borderRadius: '6px 6px 0 0',
              padding: '12px',
              marginBottom: 0,
              display: 'flex',
              gap: 8,
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
            {/* 텍스트 스타일 버튼들 */}
            <Button 
              size="small" 
              onClick={() => applyStyle('진하게', form, contentRef)}
              title={t('editor.bold')}
            >
              {t('editor.bold')}
            </Button>
            <Button 
              size="small" 
              onClick={() => applyStyle('왼쪽', form, contentRef)}
              title={t('editor.alignLeft')}
            >
              {t('editor.alignLeft')}
            </Button>
            <Button 
              size="small" 
              onClick={() => applyStyle('가운데', form, contentRef)}
              title={t('editor.alignCenter')}
            >
              {t('editor.alignCenter')}
            </Button>
            <Button 
              size="small" 
              onClick={() => applyStyle('오른쪽', form, contentRef)}
              title={t('editor.alignRight')}
            >
              {t('editor.alignRight')}
            </Button>
            
            {/* 구분선 */}
            <div style={{ width: '1px', backgroundColor: '#d9d9d9', margin: '0 4px', height: '20px' }} />
            
            {/* 법률 문서 스타일 버튼들 */}
            <Button 
              size="small" 
              onClick={() => applyStyle('제목', form, contentRef)}
              title={t('editor.titleStyle')}
            >
              {t('editor.title')}
            </Button>
            <Button 
              size="small" 
              onClick={() => applyStyle('장', form, contentRef)}
              title={t('editor.chapterStyle')}
            >
              {t('editor.chapter')}
            </Button>
            <Button 
              size="small" 
              onClick={() => applyStyle('조', form, contentRef)}
              title={t('editor.articleStyle')}
            >
              {t('editor.article')}
            </Button>
            <Button 
              size="small" 
              onClick={() => applyStyle('항', form, contentRef)}
              title={t('editor.paragraphStyle')}
            >
              {t('editor.paragraph')}
            </Button>
            <Button 
              size="small" 
              onClick={() => applyStyle('호', form, contentRef)}
              title={t('editor.subParagraphStyle')}
            >
              {t('editor.subParagraph')}
            </Button>
            <Button 
              size="small" 
              danger
              onClick={() => removeStyle(form, contentRef)}
              title={t('editor.removeStyle')}
            >
              {t('editor.removeStyle')}
            </Button>
            
            {/* 구분선 */}
            <div style={{ width: '1px', backgroundColor: '#d9d9d9', margin: '0 4px', height: '20px' }} />
                
            {/* 표 삽입 버튼 */}
            <Button 
              size="small" 
              onClick={() => setTableModalVisible(true)}
              title={t('editor.insertTable')}
            >
              {t('editor.table')}
            </Button>
            {/* 개정사항 표 삽입 버튼 */}
            <Button 
              size="small" 
              onClick={() => applyStyle('개정사항', form, contentRef)}
              title={t('editor.amendmentTable')}
            >
              {t('editor.amendment')}
            </Button>
            {/* 이미지 삽입 버튼 */}
            <Button
              size="small"
              icon={<PictureOutlined />}
              onClick={() => setImageModalVisible(true)}
              title={t('editor.insertImage')}
            >
              {t('editor.image')}
            </Button>
          </div>


            <div
              ref={contentRef}
              contentEditable={true}
              className="editor-content"
              style={{
                height: '600px',
                maxHeight: '600px',
                border: '1px solid #d9d9d9',
                borderTop: 'none',
                borderRadius: '0 0 6px 6px',
                padding: '16px',
                fontFamily: 'monospace',
                fontSize: '16px',
                lineHeight: '1.6',
                outline: 'none',
                overflowY: 'auto',
                backgroundColor: '#ffffff'
              }}
              onInput={(e) => {
                const target = e.target as HTMLDivElement;
                lastContentRef.current = target.innerHTML;
              }}
              onBlur={(e) => {
                const target = e.target as HTMLDivElement;
                const newContent = target.innerHTML;
                if (newContent !== lastContentRef.current) {
                  lastContentRef.current = newContent;
                  form.setFieldValue('content', newContent);
                }
              }}
            />
          </Form.Item>

          {/* 첨부파일 섹션 */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ 
              marginBottom: '8px', 
              fontWeight: '500', 
              color: 'rgba(0, 0, 0, 0.85)',
              fontSize: '14px'
            }}>
              {t('section.attachments')}
            </div>
            
            {/* 파일 업로드 */}
            <div style={{ marginBottom: 12 }}>
              <input
                type="file"
                id="file-upload"
                style={{ display: 'none' }}
                accept="*/*"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    handleFileUpload(file);
                    // 파일 선택 초기화
                    e.target.value = '';
                  }
                }}
              />
              <Button 
                icon={<Upload />} 
                loading={uploading}
                disabled={!publication}
                onClick={() => document.getElementById('file-upload')?.click()}
              >
                {t('button.fileSelect')}
              </Button>
              <Text type="secondary" style={{ marginLeft: 8, fontSize: '12px' }}>
                {!publication ? t('text.savePublicationFirst') : t('text.allFileSupport')}
              </Text>
            </div>

            {/* 첨부파일 목록 */}
            {attachments.length > 0 && (
              <List
                size="small"
                dataSource={attachments}
                renderItem={(attachment) => (
                  <List.Item
                    style={{ 
                      padding: '8px 12px', 
                      border: '1px solid #f0f0f0', 
                      borderRadius: '4px',
                      marginBottom: '4px'
                    }}
                    actions={[
                      <Button
                        key="download"
                        type="text"
                        size="small"
                        icon={<DownloadOutlined />}
                        onClick={() => handleFileDownload(attachment)}
                        title={t('button.download')}
                      />,
                      <Button
                        key="delete"
                        type="text"
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => handleFileDelete(attachment)}
                        title={t('button.delete')}
                      />
                    ]}
                  >
                    <List.Item.Meta
                      avatar={<FileOutlined style={{ fontSize: '16px', color: '#1890ff' }} />}
                      title={
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontSize: '13px' }}>
                            {attachment.file_name}
                          </span>
                          <Tag color="blue">{formatFileSize(attachment.file_size)}</Tag>
                        </div>
                      }
                      description={
                        <Text type="secondary" style={{ fontSize: '11px' }}>
                          {dayjs(attachment.uploaded_at).format('YYYY-MM-DD HH:mm')}
                        </Text>
                      }
                    />
                  </List.Item>
                )}
              />
            )}

            {attachments.length === 0 && (
              <div style={{ 
                textAlign: 'center', 
                padding: '20px', 
                color: '#8c8c8c',
                border: '1px dashed #d9d9d9',
                borderRadius: '4px',
                backgroundColor: '#fafafa'
              }}>
                <FileOutlined style={{ fontSize: '24px', marginBottom: '8px' }} />
                <div>{t('text.noAttachments')}</div>
              </div>
            )}
          </div>
        </Form>
      </Card>

      {/* 이미지 크기 조절 모달 */}
      <Modal
        title={t('modal.imageResizeTitle')}
        open={imageResizeModalVisible}
        onOk={handleImageResize}
        onCancel={() => {
          setImageResizeModalVisible(false);
          setSelectedImage(null);
        }}
        width={500}
        footer={[
          <Button key="auto" onClick={handleAutoResize}>
            {t('button.autoResize')}
          </Button>,
          <Button key="cancel" onClick={() => {
            setImageResizeModalVisible(false);
            setSelectedImage(null);
          }}>
            {t('button.cancel')}
          </Button>,
          <Button key="submit" type="primary" onClick={handleImageResize}>
            {t('button.apply')}
          </Button>
        ]}
      >
        <Form layout="vertical">
          <Form.Item label={t('form.currentImage')}>
            {selectedImage && (
              <div style={{ 
                border: '1px solid #d9d9d9', 
                borderRadius: '6px', 
                padding: '8px',
                textAlign: 'center',
                backgroundColor: '#fafafa'
              }}>
                <img
                  src={selectedImage.src}
                  alt={t('text.selectedImage')}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '150px',
                    objectFit: 'contain'
                  }}
                />
              </div>
            )}
          </Form.Item>
          
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label={t('form.width')}>
                <InputNumber
                  value={imageWidth}
                  onChange={(value) => setImageWidth(value || 300)}
                  min={50}
                  max={800}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label={t('form.height')}>
                <InputNumber
                  value={imageHeight}
                  onChange={(value) => setImageHeight(value || 200)}
                  min={50}
                  max={600}
                />
              </Form.Item>
            </Col>
          </Row>
          
          <div style={{ marginTop: 16, padding: '12px', backgroundColor: '#f6f8fa', borderRadius: '6px' }}>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              💡 <strong>{t('text.autoResize')}</strong>: {t('text.autoResizeDescription')}
            </Text>
          </div>
        </Form>
      </Modal>

      {/* 이미지 삽입 모달 */}
      <Modal
        title={t('modal.imageInsertTitle')}
        open={imageModalVisible}
        onOk={handleInsertImage}
        onCancel={() => {
          setImageModalVisible(false);
          setImageUrl('');
          setImageFile(null);
          setImagePreview('');
        }}
        width={600}
      >
        <Form layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label={t('form.imageUrl')}>
                <Input
                  value={imageUrl}
                  onChange={(e) => setImageUrl(e.target.value)}
                  placeholder={t('input.imageUrl')}
                  disabled={!!imageFile}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label={t('form.localFile')}>
                <Upload
                  beforeUpload={() => false}
                  onChange={({ file }) => {
                    if (file instanceof File) {
                      handleImageFileChange(file);
                    }
                  }}
                  showUploadList={false}
                  accept="image/*"
                  disabled={!!imageUrl.trim()}
                >
                  <Button disabled={!!imageUrl.trim()}>
                    {t('button.fileSelect')}
                  </Button>
                </Upload>
                {imageFile && (
                  <div style={{ marginTop: 8 }}>
                    <span style={{ fontSize: '12px', color: '#666' }}>
                      {imageFile.name}
                    </span>
                    <Button
                      type="text"
                      size="small"
                      danger
                      onClick={handleImageFileRemove}
                      style={{ marginLeft: 8 }}
                    >
                      {t('button.remove')}
                    </Button>
                  </div>
                )}
              </Form.Item>
            </Col>
          </Row>

          {/* 이미지 미리보기 */}
          {(imagePreview || imageUrl) && (
            <Form.Item label={t('form.preview')}>
              <div style={{ 
                border: '1px solid #d9d9d9', 
                borderRadius: '6px', 
                padding: '8px',
                textAlign: 'center',
                backgroundColor: '#fafafa'
              }}>
                <img
                  src={imagePreview || imageUrl}
                  alt={t('text.preview')}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '200px',
                    objectFit: 'contain'
                  }}
                />
              </div>
            </Form.Item>
          )}

          {/* 자동 크기 조정 안내 */}
          <div style={{ marginTop: 16, padding: '12px', backgroundColor: '#f0f9ff', borderRadius: '6px', border: '1px solid #bae6fd' }}>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              💡 <strong>{t('text.autoResize')}</strong>: {t('text.autoResizeDescription')}
            </Text>
          </div>
        </Form>
      </Modal>

      {/* 표 삽입 모달 */}
      <Modal
        title={t('modal.tableInsertTitle')}
        open={tableModalVisible}
        onOk={handleInsertTable}
        onCancel={() => setTableModalVisible(false)}
        okText={t('button.insert')}
        cancelText={t('button.cancel')}
        width={400}
      >
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', marginBottom: 8 }}>{t('form.rowCount')}:</label>
          <InputNumber
            min={1}
            max={20}
            value={tableRows}
            onChange={(value) => setTableRows(value || 2)}
            style={{ width: '100%' }}
          />
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', marginBottom: 8 }}>{t('form.colCount')}:</label>
          <InputNumber
            min={1}
            max={10}
            value={tableCols}
            onChange={(value) => setTableCols(value || 2)}
            style={{ width: '100%' }}
          />
        </div>
        <div style={{ fontSize: '12px', color: '#666' }}>
          * {t('text.tableInsertDescription')}
        </div>
      </Modal>
    </div>
  );
};

export default PublicationEdit; 